import { API, WS_URL } from '@/lib/api';
import { useState, useEffect, useRef, useCallback } from "react";
import { getErrorMessage } from "@/lib/utils";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Send, MapPin, Package, Truck, Car, Home, Briefcase, Wrench,
  Check, CheckCheck, Lock, Navigation, User, Loader2, ImagePlus, X, ZoomIn
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";



const SERVICE_ICONS = {
  cargo_delivery: Truck, courier: Package, towing: Car,
  movers: Home, laborer: Briefcase, roadside_assistance: Wrench,
};
const SERVICE_LABELS = {
  cargo_delivery: "Cargo Delivery", courier: "Courier", towing: "Towing",
  movers: "Movers", laborer: "Laborer", roadside_assistance: "Roadside Assistance",
};
const QUICK_REPLIES = [
  { text: "On my way", icon: Navigation },
  { text: "I've arrived", icon: MapPin },
  { text: "Where are you?", icon: User },
];
const REACTION_EMOJIS = [
  { key: "thumbsup", label: "\ud83d\udc4d" },
  { key: "thumbsdown", label: "\ud83d\udc4e" },
  { key: "heart", label: "\u2764\ufe0f" },
  { key: "laugh", label: "\ud83d\ude02" },
  { key: "wow", label: "\ud83d\ude2e" },
];
const EMOJI_DISPLAY = { thumbsup: "\ud83d\udc4d", thumbsdown: "\ud83d\udc4e", heart: "\u2764\ufe0f", laugh: "\ud83d\ude02", wow: "\ud83d\ude2e" };

const StatusIcon = ({ status }) => {
  if (status === "read") return <CheckCheck className="w-3.5 h-3.5 text-blue-400" />;
  if (status === "delivered") return <CheckCheck className="w-3.5 h-3.5 text-stone-400" />;
  return <Check className="w-3.5 h-3.5 text-stone-400" />;
};

const TypingIndicator = () => (
  <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 5 }} className="flex gap-2 items-end">
    <div className="w-7 h-7 rounded-full bg-stone-200 flex items-center justify-center shrink-0">
      <User className="w-3.5 h-3.5 text-stone-500" />
    </div>
    <div className="bg-white border border-stone-200 rounded-2xl rounded-bl-md px-4 py-2.5">
      <div className="flex items-center gap-1">
        <div className="w-1.5 h-1.5 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
        <div className="w-1.5 h-1.5 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
        <div className="w-1.5 h-1.5 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
      </div>
    </div>
  </motion.div>
);

// Image Lightbox
const ImageLightbox = ({ src, onClose }) => (
  <motion.div
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
    onClick={onClose}
    data-testid="image-lightbox"
  >
    <button onClick={onClose} className="absolute top-4 right-4 text-white/80 hover:text-white z-10">
      <X className="w-8 h-8" />
    </button>
    <img src={src} alt="Shared" className="max-w-full max-h-[85vh] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
  </motion.div>
);

// Reaction Picker popup
const ReactionPicker = ({ onSelect, onClose }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.8, y: 5 }}
    animate={{ opacity: 1, scale: 1, y: 0 }}
    exit={{ opacity: 0, scale: 0.8, y: 5 }}
    className="absolute bottom-full mb-1 bg-white rounded-full shadow-lg border border-stone-200 px-2 py-1 flex gap-0.5 z-50"
    data-testid="reaction-picker"
  >
    {REACTION_EMOJIS.map((r) => (
      <button
        key={r.key}
        className="w-8 h-8 flex items-center justify-center text-lg hover:scale-125 transition-transform rounded-full hover:bg-stone-100"
        onClick={(e) => { e.stopPropagation(); onSelect(r.key); onClose(); }}
        data-testid={`react-${r.key}`}
      >
        {r.label}
      </button>
    ))}
  </motion.div>
);

// Reactions display below message
const ReactionsDisplay = ({ reactions }) => {
  if (!reactions || reactions.length === 0) return null;
  // Group by emoji
  const grouped = {};
  reactions.forEach((r) => {
    grouped[r.emoji] = grouped[r.emoji] || { count: 0, names: [] };
    grouped[r.emoji].count++;
    grouped[r.emoji].names.push(r.name);
  });
  return (
    <div className="flex gap-1 mt-1" data-testid="message-reactions">
      {Object.entries(grouped).map(([emoji, data]) => (
        <span
          key={emoji}
          className="inline-flex items-center gap-0.5 bg-white/90 border border-stone-200 rounded-full px-1.5 py-0.5 text-xs shadow-sm"
          title={data.names.join(", ")}
        >
          {EMOJI_DISPLAY[emoji]} {data.count > 1 && <span className="text-[10px] text-stone-500">{data.count}</span>}
        </span>
      ))}
    </div>
  );
};

const ChatPage = () => {
  const { requestId } = useParams();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [request, setRequest] = useState(null);
  const [partner, setPartner] = useState(null);
  const [chatActive, setChatActive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isPartnerTyping, setIsPartnerTyping] = useState(false);
  const [activeReactionPicker, setActiveReactionPicker] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingSentRef = useRef(0);
  const fileInputRef = useRef(null);

  const myId = user?.user_id;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/chat/${requestId}`, { withCredentials: true });
        setMessages(res.data.messages || []);
        setRequest(res.data.request || null);
        setPartner(res.data.partner || null);
        setChatActive(res.data.chat_active !== false);
        axios.patch(`${API}/chat/${requestId}/read`, {}, { withCredentials: true }).catch(() => {});
      } catch (err) {
        toast.error(getErrorMessage(err, "Failed to load chat"));
      }
      setLoading(false);
    };
    fetchData();
  }, [requestId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isPartnerTyping]);

  // Close reaction picker on outside click
  useEffect(() => {
    const handleClick = () => setActiveReactionPicker(null);
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  // WebSocket with auto-reconnect
  useEffect(() => {
    if (!user) return;
    let ws = null;
    let pingInterval = null;
    let reconnectTimeout = null;
    let isMounted = true;

    const connect = () => {
      const token = document.cookie.split(";").find(c => c.trim().startsWith("session_token="))?.split("=")[1];
      if (!token || !isMounted) return;
      const wsPath = user?.account_mode === "professional" ? "professional" : "customer";
      ws = new WebSocket(`${WS_URL}/api/ws/${wsPath}/${token}`);
      wsRef.current = ws;

      ws.onopen = () => {
        pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "ping" }));
        }, 25000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "new_message" && data.request_id === requestId) {
            setMessages(prev => prev.some(m => m.message_id === data.message_id) ? prev : [...prev, data]);
            axios.patch(`${API}/chat/${requestId}/read`, {}, { withCredentials: true }).catch(() => {});
          } else if (data.type === "typing" && data.request_id === requestId) {
            setIsPartnerTyping(data.is_typing);
            if (data.is_typing) {
              clearTimeout(typingTimeoutRef.current);
              typingTimeoutRef.current = setTimeout(() => setIsPartnerTyping(false), 4000);
            }
          } else if (data.type === "messages_read" && data.request_id === requestId) {
            setMessages(prev => prev.map(m => m.sender_id === myId ? { ...m, status: "read" } : m));
          } else if (data.type === "reaction_update" && data.request_id === requestId) {
            setMessages(prev => prev.map(m => m.message_id === data.message_id ? { ...m, reactions: data.reactions } : m));
          }
        } catch {}
      };

      ws.onclose = () => {
        clearInterval(pingInterval);
        if (isMounted) reconnectTimeout = setTimeout(connect, 3000);
      };

      ws.onerror = () => {
        ws.close();
      };
    };

    connect();

    return () => {
      isMounted = false;
      clearInterval(pingInterval);
      clearTimeout(reconnectTimeout);
      clearTimeout(typingTimeoutRef.current);
      if (ws) ws.close();
    };
  }, [user, requestId, myId]);

  const sendTypingIndicator = useCallback((isTyping) => {
    const now = Date.now();
    if (isTyping && now - lastTypingSentRef.current < 2000) return;
    lastTypingSentRef.current = now;
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "typing", request_id: requestId, is_typing: isTyping }));
    }
  }, [requestId]);

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    if (e.target.value.trim()) sendTypingIndicator(true);
  };

  const sendMessage = async (text, isQuickReply = false, imgUrl = null) => {
    const msgText = text || newMessage.trim();
    if ((!msgText && !imgUrl) || sending || !chatActive) return;
    setSending(true);
    sendTypingIndicator(false);
    try {
      const res = await axios.post(`${API}/chat/send`, {
        request_id: requestId,
        message: msgText || (imgUrl ? "Sent a photo" : ""),
        is_quick_reply: isQuickReply,
        image_url: imgUrl || null,
      }, { withCredentials: true });
      setMessages(prev => [...prev, res.data]);
      if (!isQuickReply && !imgUrl) setNewMessage("");
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to send"));
    }
    setSending(false);
    inputRef.current?.focus();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage();
  };

  const handleReaction = async (messageId, emoji) => {
    try {
      const res = await axios.post(`${API}/chat/${messageId}/react`, { emoji }, { withCredentials: true });
      setMessages(prev => prev.map(m => m.message_id === messageId ? { ...m, reactions: res.data.reactions } : m));
    } catch (err) {
      toast.error(getErrorMessage(err, "Reaction failed"));
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image must be under 10MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setImagePreview({ file, dataUrl: reader.result });
    reader.readAsDataURL(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleImageSend = async () => {
    if (!imagePreview || uploadingImage) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", imagePreview.file);
      const uploadRes = await axios.post(`${API}/chat/upload-image`, formData, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" },
      });
      await sendMessage("", false, uploadRes.data.url);
      setImagePreview(null);
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to upload image"));
    }
    setUploadingImage(false);
  };

  const ServiceIcon = request ? (SERVICE_ICONS[request.service_type] || Package) : Package;
  const backLink = user?.account_mode === "professional" ? "/professional/dashboard" : "/my-bookings";
  const statusColors = {
    accepted: "bg-emerald-100 text-emerald-700", en_route: "bg-blue-100 text-blue-700",
    arrived: "bg-amber-100 text-amber-700", completed: "bg-stone-100 text-stone-600",
    cancelled: "bg-red-100 text-red-600", pending: "bg-yellow-100 text-yellow-700",
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50" data-testid="chat-page-loading">
        <Navbar />
        <div className="flex items-center justify-center pt-40">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100 flex flex-col" data-testid="chat-page">
      <Navbar />

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxImage && <ImageLightbox src={lightboxImage} onClose={() => setLightboxImage(null)} />}
      </AnimatePresence>

      <div className="pt-20 flex-1 flex flex-col max-w-3xl mx-auto w-full">
        {/* Header */}
        <div className="bg-white border-b border-stone-200 px-4 py-3 sticky top-20 z-40" data-testid="chat-header">
          <div className="flex items-center gap-3">
            <Link to={backLink} className="text-stone-400 hover:text-emerald-950 transition-colors" data-testid="chat-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center shrink-0">
              {partner?.picture ? (
                <img src={partner.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <ServiceIcon className="w-5 h-5 text-emerald-600" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-semibold text-emerald-950 text-sm truncate" data-testid="chat-title">
                {partner?.name || "Chat"}
              </h2>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 capitalize" data-testid="partner-role">
                  {partner?.role === "professional" ? "Driver" : "Client"}
                </Badge>
                {request && <span className="text-[10px] text-stone-400">{SERVICE_LABELS[request.service_type] || "Service"}</span>}
              </div>
            </div>
            {request && (
              <Badge className={`text-xs ${statusColors[request.status] || "bg-stone-100 text-stone-600"}`} data-testid="job-status-badge">
                {request.status}
              </Badge>
            )}
          </div>
          {request?.pickup_address && (
            <div className="mt-2 flex items-center gap-2 text-xs text-stone-500 bg-stone-50 rounded-lg px-3 py-2">
              <MapPin className="w-3 h-3 text-emerald-600 shrink-0" />
              <span className="truncate">{request.pickup_address}</span>
              {request.dropoff_address && (
                <>
                  <span className="text-stone-300 shrink-0">&rarr;</span>
                  <span className="truncate">{request.dropoff_address}</span>
                </>
              )}
            </div>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2" data-testid="messages-container" style={{ minHeight: "300px", maxHeight: "calc(100vh - 320px)" }}>
          <div className="text-center mb-4">
            <span className="text-[10px] bg-stone-200/60 text-stone-500 px-3 py-1 rounded-full" data-testid="system-message">
              Chat started &middot; All messages are private and secure
            </span>
          </div>

          {messages.length === 0 && (
            <div className="text-center py-8">
              <User className="w-10 h-10 text-stone-300 mx-auto mb-2" />
              <p className="text-stone-400 text-sm">No messages yet.</p>
              <p className="text-stone-300 text-xs mt-1">Use quick replies or type a message below</p>
            </div>
          )}

          {messages.map((msg, i) => {
            const isMine = msg.sender_id === myId;
            const showTimestamp = i === 0 ||
              new Date(msg.created_at).getTime() - new Date(messages[i - 1]?.created_at).getTime() > 300000;
            const prevSameSender = i > 0 && messages[i - 1]?.sender_id === msg.sender_id;

            return (
              <div key={msg.message_id || i}>
                {showTimestamp && (
                  <div className="text-center my-3">
                    <span className="text-[10px] text-stone-400">
                      {new Date(msg.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                )}
                <motion.div
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex items-end gap-1.5 ${isMine ? "justify-end" : "justify-start"} ${prevSameSender ? "mt-0.5" : "mt-2"}`}
                  data-testid={`message-${msg.message_id || i}`}
                >
                  {!isMine && !prevSameSender && (
                    <div className="w-7 h-7 rounded-full bg-stone-200 flex items-center justify-center shrink-0 overflow-hidden">
                      {msg.sender_picture ? (
                        <img src={msg.sender_picture} alt="" className="w-7 h-7 rounded-full object-cover" />
                      ) : (
                        <User className="w-3.5 h-3.5 text-stone-500" />
                      )}
                    </div>
                  )}
                  {!isMine && prevSameSender && <div className="w-7 shrink-0" />}
                  <div className="relative max-w-[75%]">
                    <div
                      className={`${msg.is_quick_reply
                        ? (isMine ? "bg-emerald-700 text-white" : "bg-blue-50 border border-blue-200 text-blue-800")
                        : (isMine ? "bg-emerald-600 text-white" : "bg-white border border-stone-200 text-stone-800")
                      } rounded-2xl px-3.5 py-2 ${isMine ? "rounded-br-md" : "rounded-bl-md"} cursor-pointer select-none`}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        if (chatActive) setActiveReactionPicker(activeReactionPicker === msg.message_id ? null : msg.message_id);
                      }}
                    >
                      {!isMine && !prevSameSender && (
                        <p className="text-[10px] font-semibold mb-0.5 opacity-60">{msg.sender_name}</p>
                      )}
                      {/* Image */}
                      {msg.image_url && (
                        <div
                          className="mb-1.5 cursor-pointer relative group"
                          onClick={() => setLightboxImage(msg.image_url)}
                          data-testid={`msg-image-${msg.message_id}`}
                        >
                          <img
                            src={msg.image_url}
                            alt="Shared"
                            className="rounded-xl max-w-full max-h-48 object-cover"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20 rounded-xl">
                            <ZoomIn className="w-6 h-6 text-white" />
                          </div>
                        </div>
                      )}
                      {/* Text (hide default "Sent a photo" if there's an image) */}
                      {(!msg.image_url || msg.message !== "Sent a photo") && (
                        <p className="text-sm leading-relaxed">{msg.message}</p>
                      )}
                      <div className={`flex items-center gap-1 justify-end mt-0.5 ${isMine ? "text-emerald-200" : "text-stone-400"}`}>
                        <span className="text-[10px]">
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </span>
                        {isMine && <StatusIcon status={msg.status} />}
                      </div>
                    </div>

                    {/* Reaction picker */}
                    <AnimatePresence>
                      {activeReactionPicker === msg.message_id && (
                        <ReactionPicker
                          onSelect={(emoji) => handleReaction(msg.message_id, emoji)}
                          onClose={() => setActiveReactionPicker(null)}
                        />
                      )}
                    </AnimatePresence>

                    {/* Reactions display */}
                    <ReactionsDisplay reactions={msg.reactions} />
                  </div>
                </motion.div>
              </div>
            );
          })}

          <AnimatePresence>
            {isPartnerTyping && <TypingIndicator />}
          </AnimatePresence>
          <div ref={messagesEndRef} />
        </div>

        {/* Chat Closed */}
        {!chatActive && (
          <div className="bg-stone-200/80 px-4 py-3 flex items-center justify-center gap-2 text-sm text-stone-600" data-testid="chat-closed-banner">
            <Lock className="w-4 h-4" />
            <span>This chat has ended. The job is {request?.status || "closed"}.</span>
          </div>
        )}

        {/* Image Preview */}
        <AnimatePresence>
          {imagePreview && (
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
              className="bg-white border-t border-stone-200 px-4 py-3"
              data-testid="image-preview"
            >
              <div className="flex items-end gap-3">
                <div className="relative">
                  <img src={imagePreview.dataUrl} alt="Preview" className="w-20 h-20 rounded-xl object-cover border border-stone-200" />
                  <button
                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                    onClick={() => setImagePreview(null)}
                    data-testid="cancel-image-btn"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
                  onClick={handleImageSend}
                  disabled={uploadingImage}
                  data-testid="send-image-btn"
                >
                  {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                  {uploadingImage ? "Sending..." : "Send Photo"}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Quick Replies */}
        {chatActive && !imagePreview && (
          <div className="bg-white border-t border-stone-100 px-4 py-2 flex gap-2 overflow-x-auto scrollbar-hide" data-testid="quick-replies">
            {QUICK_REPLIES.map((qr) => {
              const QIcon = qr.icon;
              return (
                <button
                  key={qr.text}
                  className="shrink-0 flex items-center gap-1.5 text-xs bg-stone-50 border border-stone-200 rounded-full px-3 py-1.5 text-stone-600 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 transition-colors disabled:opacity-50"
                  onClick={() => sendMessage(qr.text, true)}
                  disabled={sending}
                  data-testid={`quick-reply-${qr.text.toLowerCase().replace(/[^a-z]/g, "-")}`}
                >
                  <QIcon className="w-3 h-3" />
                  {qr.text}
                </button>
              );
            })}
          </div>
        )}

        {/* Input */}
        {chatActive ? (
          <form onSubmit={handleSubmit} className="bg-white border-t border-stone-200 px-4 py-3" data-testid="chat-input-form">
            <input type="file" ref={fileInputRef} accept="image/*" className="hidden" onChange={handleImageSelect} data-testid="image-file-input" />
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="text-stone-400 hover:text-emerald-600 transition-colors p-1"
                onClick={() => fileInputRef.current?.click()}
                disabled={sending || uploadingImage}
                data-testid="attach-image-btn"
              >
                <ImagePlus className="w-5 h-5" />
              </button>
              <input
                ref={inputRef}
                type="text"
                value={newMessage}
                onChange={handleInputChange}
                onBlur={() => sendTypingIndicator(false)}
                placeholder="Type a message..."
                className="flex-1 bg-stone-50 border border-stone-200 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 transition-colors"
                autoFocus
                disabled={sending}
                data-testid="chat-message-input"
              />
              <Button
                type="submit"
                size="icon"
                disabled={!newMessage.trim() || sending}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full w-10 h-10 shrink-0"
                data-testid="send-message-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </form>
        ) : null}
      </div>
    </div>
  );
};

export default ChatPage;
